import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/dashboard/shared/PageHeader';
import { KpiCard } from '@/components/dashboard/shared/KpiCard';
import { KpiGrid } from '@/components/dashboard/shared/KpiGrid';
import { ProductList } from '@/components/dashboard/products/ProductList';
import { DashboardHead } from '@/components/SEO/DashboardHead';
import api from '@/utils/api';
import { useCurrentStore } from '@/hooks/useCurrentStore';
import { useAuth } from '@/contexts/AuthContext';
import { isPaidAccess } from '@/lib/plan';
import { Link } from 'react-router-dom';
import {
  RiAddLine,
  RiSettings3Line,
  RiArchiveLine,
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiAlertLine,
  RiStackLine,
} from '@remixicon/react';

type ProductStats = {
  totalProducts: number;
  activeProducts: number;
  outOfStock: number;
  lowStock: number;
};

export function ProductsPage() {
  const navigate = useNavigate();
  const { storeId } = useCurrentStore();
  const { user, currentStore } = useAuth();
  const showBulk =
    currentStore?.isOwner && isPaidAccess(user);

  const { data: stats, isLoading } = useQuery<ProductStats>({
    queryKey: ['products-stats', storeId],
    queryFn: async () => {
      const { data } = await api.get('/api/analytics/products/stats');
      return data;
    },
    enabled: !!storeId,
  });

  return (
    <div className="space-y-6">
      <DashboardHead pageName="Products" />
      <PageHeader
        title="Products"
        actions={
          <>
            {showBulk && (
              <Button
                variant="outline"
                asChild
                className="h-auto gap-1 rounded-full !px-4 !py-2 text-sm font-semibold"
              >
                <Link to="/dashboard/bulk-upload">Bulk upload</Link>
              </Button>
            )}
            <Button
              variant="outline"
              asChild
              className="h-auto gap-1 rounded-full !px-4 !py-2 text-sm font-semibold"
            >
              <Link to="/dashboard/inventory">
                <RiStackLine className="h-4 w-4" />
                Inventory
              </Link>
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard/categories')}
              className="h-auto gap-1 rounded-full !px-4 !py-2 text-sm font-semibold"
            >
              <RiSettings3Line className="h-4 w-4" />
              Categories
            </Button>
            <Button
              onClick={() => navigate('/dashboard/products/new')}
              className="h-auto gap-1 rounded-full !px-4 !py-2 text-sm font-semibold shadow-none"
            >
              <RiAddLine className="h-4 w-4" />
              Add Product
            </Button>
          </>
        }
      />

      <KpiGrid loading={isLoading} skeletonCount={4}>
        <KpiCard
          label="Total Products"
          value={stats?.totalProducts ?? 0}
          icon={<RiArchiveLine />}
        />
        <KpiCard
          label="Active Products"
          value={stats?.activeProducts ?? 0}
          icon={<RiCheckboxCircleLine />}
        />
        <Link to="/dashboard/inventory?stockStatus=out_of_stock" className="min-w-0">
          <KpiCard
            label="Out of Stock"
            value={stats?.outOfStock ?? 0}
            icon={<RiErrorWarningLine />}
          />
        </Link>
        <Link to="/dashboard/inventory?stockStatus=low_stock" className="min-w-0">
          <KpiCard
            label="Low Stock"
            value={stats?.lowStock ?? 0}
            icon={<RiAlertLine />}
          />
        </Link>
      </KpiGrid>

      <ProductList />
    </div>
  );
}
